<template>
    <b-overlay :show="false" rounded="sm">
        <div style="min-height: 100vh;">
            <div class="mb-4">
                <h4 class="ctc-dashboard-title mb-1">Annual CTC Reports</h4>
                <div class="ctc-dashboard-subtitle">Employee wise Fixed vs Actual CTC Comparison</div>
            </div>
        <!-- Filters & Settings (Standard Mode). The frame puts this on a flat #F5F5F5
             panel — no card border or shadow — with the month and year picked
             separately and the end of the window shown read-only beside them. -->
        <div class="ctc-filter-panel mb-4" v-if="mode != 2">
            <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap" style="gap: 16px;">
                <h6 class="ctc-filter-title mb-0 d-flex align-items-center">
                    Report Period
                </h6>
                <div class="d-flex flex-wrap" style="gap: 12px;">
                    <!-- Button treatments are the Employee Management ones: the
                         secondary action wears "Bulk Changes" (.custom-wizard-btn),
                         the primary action wears "Add Employee" (plain b-button).
                         Those system classes already carry the frame's colours and
                         their hover states, so nothing is restyled locally here. -->
                    <b-button v-if="$acl && $acl['cost-to-company'] == 2" class="btn-primary custom-wizard-btn" @click="showUploadModal('payment')">
                        <b-icon icon="upload" class="mr-2"></b-icon> Upload Payment Data
                    </b-button>
                    <b-button v-if="$acl && $acl['cost-to-company'] >= 1" @click="generateReport">
                        <b-icon icon="graph-up" class="mr-2"></b-icon> Generate Projection
                    </b-button>
                </div>
            </div>
            <div class="ctc-filter-row">
                <div class="ctc-filter-field">
                    <label class="filter-label">From Month</label>
                    <b-form-select class="filter-select" v-model="fromMonth" :options="monthOptions"></b-form-select>
                </div>
                <div class="ctc-filter-field">
                    <label class="filter-label">From Year</label>
                    <b-form-select class="filter-select" v-model="fromYear" :options="yearOptions"></b-form-select>
                </div>
                <div class="ctc-filter-field">
                    <label class="filter-label">To</label>
                    <b-form-select class="filter-select filter-select-locked" disabled :options="[{ value: toMonthLabel, text: toMonthLabel }]" :value="toMonthLabel"></b-form-select>
                </div>
                <div class="ctc-filter-field ctc-filter-field-wide">
                    <label class="filter-label">Department</label>
                    <b-form-select class="filter-select" v-model="selectedDepartmentId" :options="[{ value: '', text: 'All Departments' }].concat(departments)"></b-form-select>
                </div>
            </div>
        </div>

        <!-- Cost Mapping Section (Manual Mode) -->
        <div class="card shadow-sm border ctc-rounded-lg mb-4" v-if="mode == 2">
            <div class="card-header bg-white border-bottom-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center flex-wrap" style="gap: 16px;">
                <h6 class="font-weight-bold ctc-card-title mb-0">Cost Mapping</h6>
                <div class="statement-tab-container">
                    <b-button 
                        :class="['statement-tab-btn', activeStatementTab === 'compensation' ? 'active' : 'inactive']" 
                        @click="switchStatementTab('compensation')"
                    >
                        Compensation Statement
                    </b-button>
                    <b-button 
                        :class="['statement-tab-btn', activeStatementTab === 'annual_year' ? 'active' : 'inactive']" 
                        @click="switchStatementTab('annual_year')"
                    >
                        Annual Year Statement
                    </b-button>
                </div>
            </div>
            <!-- Cost Mapping & Overhead Cost Grid. The frame separates this from
                 the card header with a single hairline; the two blocks below sit
                 on the card's own white, with no panel of their own. -->
            <div class="px-4 pb-4 ctc-cost-mapping-body">
                <div class="row" style="margin: 0 -8px;">
                    <!-- Cost Mapping Container -->
                    <div :class="activeStatementTab === 'annual_year' ? 'col-md-6' : 'col-12'" style="padding: 0 8px;">
                        <div class="h-100 d-flex justify-content-between align-items-center flex-wrap" style="gap: 16px;">
                            <div>
                                <h6 class="font-weight-bold ctc-cost-mapping-label small text-uppercase mb-3">Cost Mapping</h6>
                                <div class="d-flex flex-wrap" style="gap: 12px;">
                                    <b-button class="custom-btn-teal" @click="exportManualCtc(activeStatementTab)">
                                        <b-icon icon="code" class="mr-1"></b-icon> Template
                                    </b-button>
                                    <b-button v-if="$acl && $acl['cost-to-company'] == 2" class="custom-btn-teal" @click="showUploadModal('compensation_mapping')">
                                        <b-icon icon="upload" class="mr-1"></b-icon> Import
                                    </b-button>
                                    <b-button class="custom-btn-teal" @click="downloadLetters()">
                                        <b-icon icon="download" class="mr-1"></b-icon> Download Letters
                                    </b-button>
                                    <b-button v-if="$acl && $acl['cost-to-company'] == 2" class="custom-btn-teal" @click="emailLetters()">
                                        <b-icon icon="envelope" class="mr-1"></b-icon> Email Letters
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Overhead Cost Container -->
                    <div v-if="activeStatementTab === 'annual_year'" class="col-md-6 mt-3 mt-md-0 ctc-cost-overhead-col" style="padding: 0 8px;">
                        <div class="h-100">
                            <h6 class="font-weight-bold ctc-cost-mapping-label small text-uppercase mb-3">Overhead Cost</h6>
                            <div class="d-flex flex-wrap" style="gap: 12px;">
                                <b-button class="custom-btn-teal" @click="exportManualOverhead()">
                                    <b-icon icon="code" class="mr-1"></b-icon> Template
                                </b-button>
                                <b-button v-if="$acl && $acl['cost-to-company'] == 2" class="custom-btn-teal" @click="showUploadModal('overhead')">
                                    <b-icon icon="upload" class="mr-1"></b-icon> Import
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Summary Cards. Title, value and caption stack on the left with the tinted
             47px icon badge on the right, per the frame. -->
        <div class="row mb-4 ctc-summary-row" v-if="reportDataLoaded && mode == 1">
            <div class="col-md-3 mb-3 mb-md-0">
                <div class="ctc-summary-card h-100">
                    <div class="ctc-summary-body">
                        <div class="ctc-summary-title">Total Fixed CTC</div>
                        <div>
                            <div class="ctc-summary-value">৳{{ formatAmount(totalProjection) }}</div>
                            <div class="ctc-summary-sub">Full Projection</div>
                        </div>
                    </div>
                    <div class="ctc-summary-badge ctc-summary-badge-blue">
                        <b-icon icon="credit-card"></b-icon>
                    </div>
                </div>
            </div>
            <div class="col-md-3 mb-3 mb-md-0">
                <div class="ctc-summary-card h-100">
                    <div class="ctc-summary-body">
                        <div class="ctc-summary-title">Total Actual CTC</div>
                        <div>
                            <div class="ctc-summary-value">৳{{ formatAmount(totalActual) }}</div>
                            <div class="ctc-summary-sub">Prorated Projection</div>
                        </div>
                    </div>
                    <div class="ctc-summary-badge ctc-summary-badge-teal">
                        <span class="ctc-summary-taka">৳</span>
                    </div>
                </div>
            </div>
            <div class="col-md-3 mb-3 mb-md-0">
                <div class="ctc-summary-card h-100">
                    <div class="ctc-summary-body">
                        <div class="ctc-summary-title">Paid Amount</div>
                        <div>
                            <div class="ctc-summary-value">৳{{ formatAmount(totalPaid) }}</div>
                            <div class="ctc-summary-sub">Actual paid amount</div>
                        </div>
                    </div>
                    <div class="ctc-summary-badge ctc-summary-badge-indigo">
                        <b-icon icon="check2"></b-icon>
                    </div>
                </div>
            </div>
            <div class="col-md-3 mb-3 mb-md-0">
                <div class="ctc-summary-card h-100">
                    <div class="ctc-summary-body">
                        <div class="ctc-summary-title">Variance</div>
                        <div>
                            <div class="ctc-summary-value" :class="variance !== 0 ? 'ctc-variance-due' : 'ctc-variance-settled'">
                                ৳{{ formatAmount(Math.abs(variance)) }}
                            </div>
                            <div class="ctc-summary-sub" :class="variance !== 0 ? 'ctc-variance-due' : 'ctc-variance-settled'">
                                {{ variance >= 0 ? '▲' : '▼' }} {{ Math.abs(variancePercentage) }}%
                            </div>
                        </div>
                    </div>
                    <div class="ctc-summary-badge ctc-summary-badge-rose">
                        <b-icon icon="graph-up"></b-icon>
                    </div>
                </div>
            </div>
        </div>

        <!-- Section heading. The search field, record counter and pager are supplied
             by <common-list> itself, so no separate search box is rendered here. -->
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-3 mt-2"
             style="gap: 12px;" v-if="mode != 2 && reportDataLoaded">
            <h5 class="section-heading ctc-breakdown-heading mb-0">Employee-wise CTC Breakdown</h5>
        </div>

        <!-- Report Table -->
        <div class="card shadow-sm border ctc-rounded-lg mb-4" v-if="reportDataLoaded || mode == 2" style="border-color: #E8E8E8 !important; overflow: hidden;">
            <div class="card-body p-0">
                <div class="p-4 border-bottom bg-white d-flex justify-content-between align-items-center" v-if="mode == 2">
                    <div class="d-flex align-items-center">
                        <h6 class="font-weight-bold ctc-card-title mb-0 mr-3" style="font-size: 16px;">
                            {{ mode == 2 ? 'Employee List' : 'Employee-wise CTC Breakdown' }}
                        </h6>
                    </div>
                </div>
                <!-- Rendered by the shared <common-list> (DemoList.vue), which is used
                     across the product and is therefore left untouched: every cell it
                     draws is plain text off the row, so the variance is built as a
                     single-line string in annualListRows(). It supplies the
                     search field, record counter, sorting, pagination and the avatar-
                     prefixed name cell — including the demo.jpg / no-image.jpg fallback
                     when an employee has no photo. `company_options` must name every
                     default column, otherwise DemoList renders the column but leaves its
                     header blank. -->
                <div class="bg-white ctc-annual-list-wrap p-3" ref="annualListWrap" v-if="mode != 2">
                    <common-list
                        :key="`ctc-annual-report-${annualListKey}`"
                        :employees="annualListRows"
                        :default_column="listDefaultColumn"
                        :appended_column="listAppendedColumn"
                        :company_options="listCompanyOptions"
                        primary_key="employee_id"
                        :is_action="1"
                        v-on:childToParent="onAnnualListEvent"
                    ></common-list>
                </div>
                <div v-else class="p-3">
                    <manual-report-list 
                        ref="manualReportList"
                        :year="selectedReportYear"
                        :type="activeStatementTab"
                        :primary_key="'employee_id'" 
                        :decimal-point-settings="decimalPointSettings"
                        v-on:childToParent="listReturn"
                        v-on:manual-data-fetched="onManualDataFetched"
                    ></manual-report-list>
                </div>
            </div>
        </div>

        <b-modal id="bv-modal-annual-report-details" size="xl" hide-footer modal-class="ctc-details-modal">
            <template #modal-title>
                <span class="ctc-details-modal-title">Employee CTC Details</span>
            </template>
            <ctc-annual-report-employee-details
                :employee="selectedEmployee"
                :from-month="selectedFrom"
                :to-month="toMonthValue"
                :labels="apiData.labels"
                :breakdown-keys="breakdownKeys"
                :salary-as-break-down-or-gross-value="salaryAsBreakDownOrGrossValue"
                :decimal-point-settings="decimalPointSettings"
            ></ctc-annual-report-employee-details>
        </b-modal>

        <!-- Employee CTC Mapping Modal -->
        <b-modal id="bv-modal-employee-ctc-mapping" size="lg" hide-footer modal-class="ctc-mapping-modal" :title="'Data of ' + employee_name + ' for ' + selectedReportYear">
            <ctc-mapping 
                v-if="modalShow"
                :employeeID="employeeID" 
                :year="selectedReportYear" 
                :type="activeStatementTab === 'compensation' ? 0 : 1"
                @close-cost-mapping-modal="closeCostMappingModal"
            ></ctc-mapping>
        </b-modal>


        <!-- Upload Payment Data Modal -->
        <b-modal 
            id="modal-upload-payment" 
            centered 
            hide-header 
            hide-footer
            modal-class="custom-upload-modal"
        >
            <div class="custom-modal-content">
                <!-- Header -->
                <div class="custom-modal-header text-center position-relative pb-3">
                    <h5 class="modal-title-custom font-weight-bold">
                        {{ mode == 2 ? 'Import CTC Data' : 'Upload Payment Data' }}
                    </h5>
                    <button type="button" class="ctc-modal-close" aria-label="Close" @click="$bvModal.hide('modal-upload-payment')">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <!-- Body -->
                <div class="custom-modal-body mt-3">
                    <div class="form-group mb-4">
                        <label class="custom-field-label d-block mb-2">Select Year</label>
                        <b-form-select 
                            v-model="uploadYear" 
                            :options="uploadYearOptions.map(y => ({ value: y, text: y }))" 
                            class="custom-select-field w-100"
                        ></b-form-select>
                    </div>

                    <div class="form-group mb-4">
                        <label class="custom-field-label d-block mb-2">Select File</label>
                        <b-form-file 
                            ref="uploadInput" 
                            accept=".xlsx, .xls" 
                            v-model="uploadFile" 
                            placeholder="Choose or drop a file here." 
                            drop-placeholder="Drop file here..."
                            class="custom-file-input-field w-100"
                        ></b-form-file>
                    </div>
                </div>

                <!-- Footer / Action Buttons -->
                <!-- Same pairing as the page header: the secondary action uses the
                     "Bulk Changes" treatment, the primary action the "Add Employee" one. -->
                <div class="custom-modal-footer d-flex justify-content-center mt-4 pt-2">
                    <button v-if="mode != 2" class="btn btn-primary custom-wizard-btn mr-3" @click="downloadTemplate">
                        <i class="fa fa-download mr-2"></i> Download Template
                    </button>
                    <button class="btn btn-secondary" @click="handleUpload">
                        <i class="fa fa-upload mr-2"></i> {{ mode == 2 ? 'Upload' : 'Upload Data' }}
                    </button>
                </div>
            </div>
        </b-modal>


        <!-- Upload Failures Modal -->
        <b-modal id="modal-upload-failures" title="Upload Skips/Failures" size="lg" ok-only ok-title="Close" centered>
            <div class="p-2">
                <div class="alert alert-warning mb-4">
                    <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                    Some records were skipped during the upload process.
                </div>
                <div class="table-responsive">
                    <table class="table table-sm border">
                        <thead class="bg-light">
                            <tr>
                                <th>Employee</th>
                                <th>Code</th>
                                <th>CTC Head</th>
                                <th>Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(fail, i) in uploadFailures" :key="i">
                                <td>{{ fail.employee }}</td>
                                <td><code>{{ fail.code }}</code></td>
                                <td>{{ fail.head }}</td>
                                <td class="text-danger small">{{ fail.reason }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </b-modal>
        <!-- Rejected Upload Modal -->
        <b-modal id="modal-upload-errors" title="Upload Rejected" size="lg" ok-only ok-title="Close" centered>
            <div class="p-2">
                <div class="alert alert-danger mb-4">
                    <b-icon icon="x-circle" class="mr-2"></b-icon>
                    {{ uploadErrorSummary || 'The file could not be imported.' }}
                </div>
                <ul class="pl-4 mb-0">
                    <li v-for="(err, i) in uploadErrors" :key="i" class="mb-2 small">{{ err }}</li>
                </ul>
            </div>
        </b-modal>
        <!-- Export Currency Modal -->
        <b-modal id="modal-employee-export" hide-footer centered size="md">
            <template #modal-title>
                <span class="font-weight-bold" style="color: #212529;">Export CTC Report</span>
            </template>
            <div class="p-3 text-center">
                <p class="text-secondary mb-3">
                    Export <strong>{{ exportEmployee ? exportEmployee.name : '' }}</strong>'s personal CTC report amount in:
                </p>
                <!-- Currency Selection Buttons -->
                <div class="d-flex justify-content-center mb-4" style="gap: 10px;">
                    <b-button 
                        v-for="curr in ['BDT', 'USD', 'CAD', 'AUD']" 
                        :key="curr" 
                        :variant="exportCurrency === curr ? 'primary' : 'outline-secondary'"
                        style="min-width: 75px; font-weight: 600; border-radius: 6px;"
                        @click="setExportCurrency(curr)"
                    >
                        {{ curr }}
                    </b-button>
                </div>

                <!-- Conversion Rate Input Box -->
                <div v-if="exportCurrency !== 'BDT'" class="form-group mb-4 text-left mx-auto" style="max-width: 280px;">
                    <label class="font-weight-bold text-dark small mb-1">Conversion Rate (1 {{ exportCurrency }} = ? Taka)</label>
                    <input 
                        type="text" 
                        class="form-control" 
                        :class="{'is-invalid': conversionRateError}"
                        v-model="conversionRate" 
                        placeholder="e.g., 127"
                        required
                    />
                    <div class="invalid-feedback">Conversion rate is required, must be a number, and greater than 0.</div>
                </div>

                <!-- Download Button -->
                <div class="d-flex justify-content-center">
                    <b-button 
                        class="btn-fixed-report px-4 d-flex align-items-center justify-content-center" 
                        style="background-color: #01524B; border-color: #01524B; font-weight: 500; height: 38px; gap: 6px;"
                        @click="triggerEmployeeExport"
                    >
                        <i class="fa fa-download"></i> Download Report
                    </b-button>
                </div>
            </div>
        </b-modal>
        </div>
    </b-overlay>
</template>

<script>
import axios from 'axios';
import AnnualReportEmployeeDetails from './AnnualReportEmployeeDetails.vue';
import ManualReportList from './ManualReportList.vue';
import ctcOverlay from '../js/ctcOverlay';

export default {
    mixins: [ctcOverlay],
    name: 'AnnualReport',
    props: {
        ctcSettings: {
            type: Object,
            default: null
        }
    },
    watch: {
        ctcSettings: {
            immediate: true,
            handler(newSettings) {
                if (newSettings) {
                    this.applyCtcSettings(newSettings);
                }
            }
        },
        // Searching, sorting and paging are handled inside <common-list> against the
        // rows it is given, so the department filter only has to change that set.
        showOverlay(newVal) {
            this.ctcOverlay(newVal);
        }
    },
    components: {
        'ctc-annual-report-employee-details': AnnualReportEmployeeDetails,
        'manual-report-list': ManualReportList
    },
    data() {
        return {
            currentYear: new Date().getFullYear(),
            selectedFrom: null,
            reportDataLoaded: false,
            departments: [],
            selectedDepartmentId: '',
            selectedEmployee: null,
            actualEmployeesById: {},
            uploadYear: new Date().getFullYear(),
            uploadFailures: [],
            uploadErrors: [],
            uploadErrorSummary: '',
            searchQuery: '',
            mode: 1,
            allEmployees: [],
            selectedEmployeeIds: [],
            selectAll: false,
            templateYear: new Date().getFullYear(),
            showOverlay: false,
            uploadFile: null,
            decimalPointSettings: 'no',
            exportEmployee: null,
            exportCurrency: 'BDT',
            conversionRate: '',
            conversionRateError: false,

            // --- <common-list> configuration (mode 1) -----------------------
            // Order here is the column order on screen. `name` must stay in
            // default_column: that is the only key DemoList renders with the
            // employee avatar (and its no-photo fallback image).
            listDefaultColumn: ['name', 'employee_code', 'department'],
            // Amounts are pre-formatted strings (৳ prefix, company decimal
            // setting), so these stay 'text' rather than DemoList's 'amount'
            // type, which would re-format them with the global money rules.
            listAppendedColumn: [
                { value: 'joining_date',     text: 'Joining Date', type: 'text' },
                { value: 'fixed_ctc',        text: 'Fixed CTC',    type: 'text' },
                { value: 'actual_ctc',       text: 'Actual CTC',   type: 'text' },
                { value: 'paid_amount',      text: 'Paid Amount',  type: 'text' },
                { value: 'variance_amount',  text: 'Variance',     type: 'text' },
            ],
            // Every default column needs a matching entry here or DemoList
            // renders an empty <th> for it. `options` is required too: the
            // filter sidebar reads `options.length` without guarding it.
            listCompanyOptions: [
                { label: 'name',          type: 'text', options: [] },
                { label: 'employee_code', type: 'text', options: [] },
                { label: 'department',    type: 'text', options: [] },
            ],
            // DemoList builds its columns once, in created(), so the list is
            // remounted whenever a fresh report arrives.
            annualListKey: 0,
            // Watches the list DOM so the variance cells keep their red/green after
            // DemoList repaints (search, sort, paging). See watchVarianceColumn().
            varianceObserver: null,


            apiData: {
                summary: {
                    company_total_projection: 0,
                    departments: {}
                },
                labels: {},
                data: []
            },
            salaryAsBreakDownOrGrossValue: 1,  // default: show breakdown
            activeStatementTab: 'compensation',
            selectedReportYear: new Date().getFullYear(),
            currentUploadType: 'payment',
            manualEmployees: [],
            manualCompanyOptions: [],
            manualAppendedColumns: [],
            list_key: 0,
            employeeID: null,
            employee_name: '',
            modalShow: false,
        }
    },
    computed: {
        totalProjection() {
            if (!this.reportDataLoaded || !this.apiData || !this.apiData.summary) return 0;
            if (!this.selectedDepartmentId) {
                return this.apiData.summary.company_total_projection || 0;
            }
            const depts = this.apiData.summary.departments_projection || {};
            return depts[String(this.selectedDepartmentId)] || 0;
        },
        totalActual() {
            if (!this.reportDataLoaded || !this.apiData || !this.apiData.summary) return 0;
            if (!this.selectedDepartmentId) {
                return this.apiData.summary.company_total_actual || 0;
            }
            const depts = this.apiData.summary.departments_actual || {};
            return depts[String(this.selectedDepartmentId)] || 0;
        },
        totalPaid() {
            if (!this.reportDataLoaded || !this.apiData || !this.apiData.summary) return 0;
            if (!this.selectedDepartmentId) {
                return this.apiData.summary.company_total_paid || 0;
            }
            const depts = this.apiData.summary.departments_paid || {};
            return depts[String(this.selectedDepartmentId)] || 0;
        },
        variance() {
            return this.totalActual - this.totalPaid;
        },
        variancePercentage() {
            if (this.totalActual === 0) return 0;
            return ((this.variance / this.totalActual) * 100).toFixed(1);
        },
        filteredEmployees() {
            if (!this.reportDataLoaded || !this.apiData || !this.apiData.data) return [];

            let filtered = this.apiData.data;

            // Department filter (Local JS filter). The free-text search is done by
            // <common-list> itself, across every column it is given.
            if (this.selectedDepartmentId) {
                filtered = filtered.filter(emp => String(emp.department_id) === String(this.selectedDepartmentId));
            }

            return filtered;
        },
        // Rows handed to <common-list>. Every displayed value has to be a plain
        // property on the row, so the amounts are formatted here; `actions` drives
        // DemoList's row menu and is reported back through childToParent.
        annualListRows() {
            return this.filteredEmployees.map(emp => ({
                ...emp,
                department: emp.department || 'Uncategorized',
                joining_date: emp.date_of_joining || 'Not set',
                fixed_ctc: '৳' + this.formatAmount(emp.total_ctc_projection || 0),
                actual_ctc: '৳' + this.formatAmount(emp.total_ctc_actual || 0),
                paid_amount: emp.total_paid_amount > 0 ? '৳' + this.formatAmount(emp.total_paid_amount) : '-',
                // The design shows the amount above a percentage pill, but DemoList
                // renders one text value per cell and is shared product-wide, so the
                // amount and the percentage (or "Settled") are joined into one line.
                variance_amount: this.employeeVarianceText(emp),
                // Action ids start at 1: DemoList sends action_id 0 for a plain cell
                // click too, so Details must not reuse 0 or the whole row would open
                // the modal. See onAnnualListEvent().
                actions: [
                    { label: 'Details', value: 2 },
                    { label: 'Export', value: 1 },
                ],
            }));
        },
        filteredAllEmployees() {
            if (!this.allEmployees) return [];
            let filtered = this.allEmployees;
            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase();
                filtered = filtered.filter(emp => 
                    (emp.name && emp.name.toLowerCase().includes(q)) || 
                    (emp.employee_code && emp.employee_code.toLowerCase().includes(q))
                );
            }
            return filtered;
        }
        ,
        // `selectedFrom` stays the single 'YYYY-MM' value everything else reads; the
        // design just picks its month and year in two separate selects.
        monthOptions() {
            return [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ].map((name, index) => ({ value: index + 1, text: name }));
        },
        yearOptions() {
            const years = [];
            for (let year = this.currentYear - 3; year <= this.currentYear + 3; year++) {
                years.push({ value: year, text: String(year) });
            }
            return years;
        },
        fromMonth: {
            get() {
                return this.selectedFrom ? Number(this.selectedFrom.split('-')[1]) : 1;
            },
            set(month) {
                this.selectedFrom = `${this.fromYear}-${String(month).padStart(2, '0')}`;
            }
        },
        fromYear: {
            get() {
                return this.selectedFrom ? Number(this.selectedFrom.split('-')[0]) : this.currentYear;
            },
            set(year) {
                this.selectedFrom = `${year}-${String(this.fromMonth).padStart(2, '0')}`;
            }
        },
        toMonthDate() {
            if (!this.selectedFrom) {
                return null;
            }
            const [year, month] = this.selectedFrom.split('-').map(Number);
            const date = new Date(year, month - 1);
            date.setMonth(date.getMonth() + 11);
            return date;
        },
        toMonthLabel() {
            const months = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];
            if (!this.toMonthDate) {
                return '';
            }
            return `${months[this.toMonthDate.getMonth()]} ${this.toMonthDate.getFullYear()}`;
        },
        toMonthValue() {
            if (!this.toMonthDate) {
                return '';
            }
            return `${this.toMonthDate.getFullYear()}-${String(this.toMonthDate.getMonth() + 1).padStart(2, '0')}`;
        },
        uploadYearOptions() {
            const currentYear = new Date().getFullYear();
            const options = [];
            // 3 past year
            options.push(currentYear - 3);
            options.push(currentYear - 2);
            options.push(currentYear - 1);
            // Current year
            options.push(currentYear);
            // 8 future years
            for (let i = 1; i <= 8; i++) {
                options.push(currentYear + i);
            }
            return options;
        },
        breakdownKeys() {
            return this.apiData.breakdown_keys || [];
        },
    },
    mounted() {
        this.getActiveDepartments();
        this.buildFromMonthOptions();
        this.loadCtcSettings();
        this.getSiteOptionSettings();
        this.$nextTick(this.watchVarianceColumn);
    },
    beforeDestroy() {
        if (this.varianceObserver) {
            this.varianceObserver.disconnect();
            this.varianceObserver = null;
        }
    },
    methods: {
        onManualDataFetched(data) {
            this.manualEmployees = data.employees;
            this.manualAppendedColumns = data.appended_column;
            this.manualCompanyOptions = data.company_options;
        },
        listReturn(data) {
            if (data.selected) {
                this.selectedEmployeeIds = data.selected;
            } else {
                this.selectedEmployeeIds = [];
            }
            if (data.employee_id > 0) {
                const employee = this.manualEmployees.find(emp => emp.employee_id === data.employee_id);
                if (employee) {
                    this.employee_name = employee.name;
                } else {
                    this.employee_name = '';
                }
                this.editEmployee(data.employee_id);
            }
        },
        editEmployee(em_id) {
            if (!this.checkNet()) return false;
            this.employeeID = em_id;
            
            let formData = new FormData();
            formData.append('employee_id', em_id);
            axios.post(this.$api_url + '/employee/setemployee', formData)
                .then(() => {
                    this.modalShow = true;
                    this.$bvModal.show('bv-modal-employee-ctc-mapping');
                })
                .catch(error => {
                    this.serverError(error);
                });
        },
        closeCostMappingModal() {
            this.modalShow = false;
            this.$bvModal.hide('bv-modal-employee-ctc-mapping');
            if (this.$refs.manualReportList) {
                this.$refs.manualReportList.getManualCtcMapping();
            }
            this.$bvToast.toast('Saved!', {
                title: `Success`,
                variant: 'success',
                solid: true
            });
        },
        switchStatementTab(tab) {
            this.activeStatementTab = tab;
        },
        overheadTemplateAction() {
            // Placeholder action
        },
        overheadImportAction() {
            // Placeholder action
        },
        // The window still travels as one 'YYYY-MM' string; the month and year are
        // only split apart for the two pickers the design asks for.
        buildFromMonthOptions() {
            if (!this.selectedFrom) {
                this.selectedFrom = `${this.currentYear}-01`;
            }
        },
        loadCtcSettings() {
            if (this.ctcSettings) {
                this.applyCtcSettings(this.ctcSettings);
            }
        },
        applyCtcSettings(settings) {
            const startMonth = Number(settings.start_month) || 1;
            const monthString = String(startMonth).padStart(2, '0');
            this.selectedFrom = `${this.currentYear}-${monthString}`;
            
            if (settings.mode) {
                this.mode = Number(settings.mode);
                if (this.mode == 2) {
                    this.loadEmployees();
                }
            }
        },
        loadEmployees() {
            axios.get('/cost-to-company/employees')
                .then(response => {
                    this.allEmployees = response.data.data || [];
                })
                .catch(error => {
                    console.error('Error loading employees:', error);
                });
        },
        toggleSelectAll() {
            if (this.selectAll) {
                this.selectedEmployeeIds = this.filteredAllEmployees.map(emp => emp.employee_id);
            } else {
                this.selectedEmployeeIds = [];
            }
        },
        downloadManualTemplate(type) {
            if (this.selectedEmployeeIds.length === 0) {
                this.$bvToast.toast('No employee selected. Please select employee(s)', {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-left'
                });
                return;
            }
            
            const params = new URLSearchParams();
            params.append('year', this.templateYear);
            params.append('type', type);
            params.append('mode', 2);
            this.selectedEmployeeIds.forEach(id => params.append('employee_ids[]', id));
            
            axios.get(`/cost-to-company/payment/template?${params.toString()}`, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `CTC_Template_${this.templateYear}.xls`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch(error => {
                    console.error('Download error:', error);
                    this.$bvToast.toast('Error downloading template', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true
                    });
                });
        },
        onReportYearChange() {
            this.currentYear = Number(this.selectedReportYear);
            this.templateYear = Number(this.selectedReportYear);
            this.uploadYear = Number(this.selectedReportYear);
            if (this.ctcSettings) {
                this.applyCtcSettings(this.ctcSettings);
            }
            this.generateReport();
        },
        getActiveDepartments() {
            let currentObj = this;
            axios.get('/cost-to-company/active-departments').then(function(response) {
                currentObj.departments = response.data;
            }).catch(function (error) { 
                console.error(error); 
            });
        },
        generateReport() {
            if (!this.$acl || this.$acl['cost-to-company'] < 1) {
                this.$bvToast.toast('You are not authorized to generate projections.', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            if (this.mode == 2) {
                return;
            }
            let currentObj = this;
            this.showOverlay = true;
            let payload = {};
            if (this.selectedFrom) {
                payload.from_month = this.selectedFrom;
                payload.to_month = this.toMonthValue;
            }
            if (this.selectedDepartmentId) {
                payload.department_id = this.selectedDepartmentId;
            }
            
            // Call both APIs
            let projectionPromise = axios.get('/cost-to-company/report/projection', { params: payload });
            let actualPromise = axios.get('/cost-to-company/report/actual', { params: payload });
            
            Promise.all([projectionPromise, actualPromise]).then(function(responses) {
                let projectionData = responses[0].data;
                let actualData = responses[1].data;
                
                if (projectionData && projectionData.data && actualData && actualData.data) {
                    // Merge the data
                    let mergedData = currentObj.mergeEmployeeData(projectionData.data, actualData.data);
                    let mergedSummary = currentObj.mergeSummaryData(projectionData.summary, actualData.summary);
                    let actualEmployeesById = {};

                    actualData.data.forEach(emp => {
                        actualEmployeesById[emp.employee_id] = emp;
                    });
                    
                    currentObj.apiData = {
                        summary: mergedSummary,
                        data: mergedData,
                        labels: projectionData.labels || {},
                        breakdown_keys: projectionData.breakdown_keys || [],  // ← store it
                    };
                    currentObj.actualEmployeesById = actualEmployeesById;
                    currentObj.reportDataLoaded = true;
                    currentObj.annualListKey++;
                } else {
                    console.error("Invalid data format received.");
                }
            }).catch(function (error) {
                console.error(error);
            }).finally(function () {
                currentObj.showOverlay = false;
            });
        },
        employeeVariance(emp) {
            return Number(emp.total_ctc_actual || 0) - Number(emp.total_paid_amount || 0);
        },
        employeeVariancePercentage(emp) {
            const actual = Number(emp.total_ctc_actual || 0);
            if (actual === 0) return 0;
            return ((this.employeeVariance(emp) / actual) * 100).toFixed(1);
        },
        // The whole variance cell as one line: the amount followed by the signed
        // percentage, or "Settled" when actual and paid match. The arrow carries the
        // direction, since a shared list cell cannot be coloured per row.
        employeeVarianceText(emp) {
            const variance = this.employeeVariance(emp);
            const amount = '৳' + this.formatAmount(Math.abs(variance));
            if (variance === 0) return amount + ' (Settled)';
            const arrow = variance >= 0 ? '▲' : '▼';
            return amount + ' (' + arrow + ' ' + Math.abs(this.employeeVariancePercentage(emp)) + '%)';
        },
        // <common-list> is shared across the product and renders every cell as plain
        // text, so it cannot be told that the variance column is red when money is
        // still owed and green when it is settled. The colour is applied from here
        // instead: the variance <td> of each rendered row is tagged after the list
        // paints, and re-tagged whenever DemoList rebuilds or rewrites the tbody
        // (search, sort, paging, a new report). Only classes are touched, so the
        // observer never reacts to its own work.
        watchVarianceColumn() {
            if (typeof MutationObserver === 'undefined' || this.varianceObserver) return;

            // Watched on the component root, not on the list wrapper: the wrapper
            // itself only appears once a report has been loaded.
            this.varianceObserver = new MutationObserver(() => this.paintVarianceColumn());
            this.varianceObserver.observe(this.$el, {
                childList: true,
                subtree: true,
                characterData: true,
            });
            this.paintVarianceColumn();
        },
        paintVarianceColumn() {
            const wrap = this.$refs.annualListWrap;
            if (!wrap) return;

            // The column position is read off the header rather than assumed, so it
            // survives DemoList's optional checkbox / extra-field columns.
            const heads = wrap.querySelectorAll('tr.table-head > td');
            let index = -1;
            for (let i = 0; i < heads.length; i++) {
                if ((heads[i].textContent || '').trim().toLowerCase().indexOf('variance') > -1) {
                    index = i;
                    break;
                }
            }
            if (index < 0) return;

            const rows = wrap.querySelectorAll('tr.table-row');
            for (let i = 0; i < rows.length; i++) {
                const cell = rows[i].children[index];
                if (!cell) continue;

                const text = (cell.textContent || '').trim();
                cell.classList.remove('ctc-variance-due', 'ctc-variance-settled');
                if (!text) continue;
                cell.classList.add(text.indexOf('Settled') > -1 ? 'ctc-variance-settled' : 'ctc-variance-due');
            }
        },
        // <common-list> reports row-menu picks here as the primary key plus the chosen
        // action id (2 = Details, 1 = Export). It also fires on any cell click with
        // action_id 0, and on selection changes with no row id — both are ignored, so
        // the details modal only opens from the row menu.
        onAnnualListEvent(value) {
            const employeeId = value && value['employee_id'];
            if (!employeeId) return;

            const actionId = Number(value['action_id']);
            if (actionId != 1 && actionId != 2) return;

            const emp = this.filteredEmployees.find(row => row.employee_id == employeeId);
            if (!emp) return;

            if (actionId == 1) {
                this.openExportModal(emp);
            } else {
                this.openEmployeeDetails(emp);
            }
        },
        async openEmployeeDetails(emp) {
            const actualSummary = this.actualEmployeesById[emp.employee_id] || null;
            this.selectedEmployee = {
                ...emp,
                actual_summary: actualSummary,
                actual_detail: null
            };
            this.$bvModal.show('bv-modal-annual-report-details');

            try {
                let payload = {
                    employee_id: emp.employee_id,
                    from_month: this.selectedFrom,
                    to_month: this.toMonthValue
                };
                let actualPromise = axios.get('/cost-to-company/report/actual/detail', { params: payload });
                let response = await actualPromise;

                this.selectedEmployee = {
                    ...this.selectedEmployee,
                    actual_detail: response.data || null
                };
            } catch (error) {
                console.error(error);
            }
        },
        downloadEmployeeExcel(emp, currency = 'BDT', rate = '') {
            const params = new URLSearchParams();
            params.append('employee_id', emp.employee_id);
            params.append('from_month', this.selectedFrom);
            params.append('to_month', this.toMonthValue);
            params.append('currency', currency);
            if (currency !== 'BDT' && rate) {
                params.append('conversion_rate', rate);
            }
            
            axios.get(`/cost-to-company/report/employee/export?${params.toString()}`, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    const employeeNameSnake = (emp.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
                    link.setAttribute('download', `CTC_Report_${employeeNameSnake}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch(error => {
                    console.error('Download error:', error);
                    this.$bvToast.toast('Error downloading report', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true
                    });
                });
        },
        openExportModal(emp) {
            this.exportEmployee = emp;
            this.exportCurrency = 'BDT';
            this.conversionRate = '';
            this.conversionRateError = false;
            this.$bvModal.show('modal-employee-export');
        },
        setExportCurrency(curr) {
            this.exportCurrency = curr;
            if (curr === 'BDT') {
                this.conversionRate = '';
                this.conversionRateError = false;
            }
        },
        triggerEmployeeExport() {
            if (this.exportCurrency !== 'BDT') {
                const valueStr = String(this.conversionRate).trim();
                const numRegex = /^[0-9]+(\.[0-9]+)?$/;
                if (!numRegex.test(valueStr)) {
                    this.conversionRateError = true;
                    return;
                }
                const rate = parseFloat(valueStr);
                if (isNaN(rate) || rate <= 0) {
                    this.conversionRateError = true;
                    return;
                }
            }
            this.conversionRateError = false;
            this.downloadEmployeeExcel(this.exportEmployee, this.exportCurrency, this.conversionRate);
            this.$bvModal.hide('modal-employee-export');
        },
        mergeEmployeeData(projectionEmployees, actualEmployees) {
            let employeeMap = {};
            
            // Add projection data
            projectionEmployees.forEach(emp => {
                employeeMap[emp.employee_id] = {
                    ...emp,
                    total_ctc_projection: emp.total_ctc_projection || 0,
                    total_ctc_actual: emp.total_ctc_actual || 0,
                    total_paid_amount: 0
                };
            });
            
            // Add actual data
            actualEmployees.forEach(emp => {
                if (employeeMap[emp.employee_id]) {
                    employeeMap[emp.employee_id].total_paid_amount = emp.total_ctc_projection || 0;
                } else {
                    employeeMap[emp.employee_id] = {
                        ...emp,
                        total_ctc_projection: 0,
                        total_ctc_actual: 0,
                        total_paid_amount: emp.total_ctc_projection || 0
                    };
                }
            });
            
            return Object.values(employeeMap);
        },
        mergeSummaryData(projectionSummary, actualSummary) {
            return {
                company_total_projection: projectionSummary.company_total_projection || 0,
                company_total_actual: projectionSummary.company_total_actual || 0,
                company_total_paid: actualSummary.company_total_projection || 0,
                departments_projection: projectionSummary.departments_projection || {},
                departments_actual: projectionSummary.departments_actual || {},
                departments_paid: actualSummary.departments || {}
            };
        },
        mergeDepartmentSummaries(projDepts, actualDepts) {
            let merged = {};
            let allDeptIds = new Set([...Object.keys(projDepts), ...Object.keys(actualDepts)]);
            
            allDeptIds.forEach(deptId => {
                merged[deptId] = {
                    projection: projDepts[deptId] || 0,
                    actual: actualDepts[deptId] || 0
                };
            });
            
            return merged;
        },
        showUploadModal(uploadType = 'payment') {
            if (!this.$acl || this.$acl['cost-to-company'] < 2) {
                this.$bvToast.toast('You are not authorized to perform upload actions.', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            this.currentUploadType = uploadType;
            this.$bvModal.show('modal-upload-payment');
        },
        exportManualCtc(type) {
            if (this.selectedEmployeeIds.length === 0) {
                this.$bvToast.toast('No employee selected. Please select employee(s)', {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-left'
                });
                return;
            }
            
            const payload = {
                year: this.uploadYear,
                type: type,
                employees: this.selectedEmployeeIds
            };
            
            axios.post('/cost-to-company/manual-ctc-mapping/export', payload, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Manual_CTC_Export_${this.uploadYear}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch(error => {
                    console.error('Export error:', error);
                    this.$bvToast.toast('Error exporting manual CTC data', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true
                    });
                });
        },
        exportManualOverhead() {
            if (this.selectedEmployeeIds.length === 0) {
                this.$bvToast.toast('No employee selected. Please select employee(s)', {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-left'
                });
                return;
            }
            
            const payload = {
                year: this.uploadYear,
                employees: this.selectedEmployeeIds
            };
            
            axios.post('/cost-to-company/manual-ctc-mapping/export-overhead', payload, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Manual_Overhead_Export_${this.uploadYear}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch(error => {
                    console.error('Export error:', error);
                    this.$bvToast.toast('Error exporting manual overhead data', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true
                    });
                });
        },
        downloadTemplate() {
            axios.get(`/cost-to-company/payment/template?year=${this.uploadYear}`, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `CTC_Template_${this.uploadYear}.xls`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch(error => {
                    console.error('Download error:', error);
                    this.$bvToast.toast('Error downloading template', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true
                    });
                });
        },
        handleUpload() {
            const file = this.uploadFile;
            if (!file) {
                this.$bvToast.toast('Please select a file to upload.', {
                    title: 'Warning',
                    variant: 'warning',
                    solid: true
                });
                return;
            }

            const validationEvent = { target: { files: [file] } };
            if (!this.checkFileType(validationEvent, 'uploadInput', ['.xlsx', '.xls'])) {
                this.uploadFile = null;
                return;
            }

            const formData = new FormData();
            formData.append('file', file);
            formData.append('year', this.uploadYear);

            let url = '/cost-to-company/payment/upload';
            if (this.currentUploadType === 'compensation_mapping') {
                url = '/cost-to-company/manual-ctc-mapping/import';
                formData.append('type', this.activeStatementTab === 'compensation' ? 0 : 1);
            } else if (this.currentUploadType === 'overhead') {
                url = '/cost-to-company/manual-ctc-mapping/import-overhead';
            }

            axios.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(response => {
                const data = response.data;

                // A rejected file is reported as status:false / 'error'; treat that as a
                // failure even when it arrives on a 200 response.
                if (data && (data.status === false || data.status === 'error')) {
                    this.showUploadRejection(data);
                    return;
                }

                this.$bvToast.toast(data.message || 'Upload successful', {
                    title: 'Success',
                    variant: 'success',
                    solid: true
                });

                if (data.failures && data.failures.length > 0) {
                    this.uploadFailures = data.failures;
                    this.$bvModal.show('modal-upload-failures');
                }

                this.$bvModal.hide('modal-upload-payment');
                this.generateReport();
                if (this.$refs.manualReportList) {
                    this.$refs.manualReportList.getManualCtcMapping();
                }
            }).catch(error => {
                const data = error.response && error.response.data;
                this.showUploadRejection(data);
                console.error(error);
            }).finally(() => {
                this.uploadFile = null;
            });
        },
        // Nothing was imported: keep the upload modal open, say why, and list the
        // problems so the file can be corrected and re-uploaded.
        showUploadRejection(data) {
            const flatten = value => {
                if (Array.isArray(value)) return value.map(flatten).join(' ');
                if (value && typeof value === 'object') return Object.values(value).map(flatten).join(' ');
                return value === null || value === undefined ? '' : String(value);
            };

            let messages = [];
            if (data && data.errors) {
                messages = (Array.isArray(data.errors) ? data.errors : Object.values(data.errors))
                    .map(flatten)
                    .filter(m => m.trim() !== '');
            }
            if (data && data.error) {
                messages.push(flatten(data.error));
            }

            const summary = (data && data.message) || messages[0] || 'Upload failed. Nothing was imported.';

            this.$bvToast.toast(summary, {
                title: 'Upload rejected',
                variant: 'danger',
                solid: true,
                autoHideDelay: 8000
            });

            if (messages.length > 0) {
                this.uploadErrors = messages;
                this.uploadErrorSummary = summary;
                this.$bvModal.show('modal-upload-errors');
            }
        },
        async getSiteOptionSettings() {
            try {
                const response = await axios.get('/cost-to-company/get-report-options');
                const siteOptions = response.data.ctc_site_options || {};
                Object.values(siteOptions).forEach(option => {
                    if (option.opt_key === 'ctc_salary_as_break_down_or_gross') {
                        this.salaryAsBreakDownOrGrossValue = Number(option.status);
                    }
                });
                // Fetch decimal point setting
                if (response.data.decimal_point_settings) {
                    this.decimalPointSettings = response.data.decimal_point_settings;
                }
            } catch (error) {
                console.error('Error fetching site option settings:', error);
            }
        },
        /**
         * Format a monetary amount according to the company decimal setting.
         * 'yes' → 2 decimal places, 'no' (default) → rounded integer.
         */
        formatAmount(value) {
            const num = Number(value || 0);
            if (this.decimalPointSettings === 'yes') {
                return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
            return Math.round(num).toLocaleString('en-IN');
        },
        downloadLetters() {
            if (this.selectedEmployeeIds.length === 0) {
                this.$bvToast.toast('No employee selected. Please select employee(s)', {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-left'
                });
                return;
            }
            
            const payload = {
                year: this.uploadYear,
                type: this.activeStatementTab === 'compensation' ? 0 : 1,
                employees: this.selectedEmployeeIds
            };
            
            this.showOverlay = true;
            
            axios.post('/cost-to-company/manual-ctc-mapping/employee-cost', payload, { responseType: 'blob' })
                .then(response => {
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    
                    const filename = this.activeStatementTab === 'compensation' 
                        ? 'Compensation_Statement.zip' 
                        : 'Annual_Year_Statement.zip';
                        
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    this.$bvToast.toast('Letters downloaded successfully', {
                        title: 'Success',
                        variant: 'success',
                        solid: true
                    });
                })
                .catch(error => {
                    console.error('Download letters error:', error);
                    this.$bvToast.toast('Error downloading letters', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true
                    });
                })
                .finally(() => {
                    this.showOverlay = false;
                });
        },
        emailLetters() {
            if (!this.$acl || this.$acl['cost-to-company'] < 2) {
                this.$bvToast.toast('You are not authorized to send email letters.', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            if (this.selectedEmployeeIds.length === 0) {
                this.$bvToast.toast('No employee selected. Please select employee(s)', {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-left'
                });
                return;
            }
            
            const payload = {
                year: this.uploadYear,
                type: this.activeStatementTab === 'compensation' ? 0 : 1,
                employees: this.selectedEmployeeIds,
                email: 1
            };
            
            this.showOverlay = true;
            
            axios.post('/cost-to-company/manual-ctc-mapping/employee-cost', payload)
                .then(response => {
                    this.$bvToast.toast('Letters emailed successfully', {
                        title: 'Success',
                        variant: 'success',
                        solid: true
                    });
                })
                .catch(error => {
                    console.error('Email letters error:', error);
                    this.$bvToast.toast('Error emailing letters', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true
                    });
                })
                .finally(() => {
                    this.showOverlay = false;
                });
        },
        // Employee photos, and the demo.jpg / no-image.jpg fallbacks when a photo is
        // missing or fails to load, are rendered by <common-list> (DemoList.vue) from
        // the `pic` and `company_id` fields on each row.
    }
}
</script>

<style scoped>
.ctc-rounded-lg {
    border-radius: 8px;
}

/* New custom styles for Cost Mapping section */

/* Manual mode's two card titles — "Cost Mapping" and "Employee List". */
.ctc-card-title {
    color: #262626;
}

/* The Cost Mapping card's two action blocks. The frame keeps the whole card on
   white and marks them off from the header with one hairline, so neither block
   carries a tint or a border of its own. */
.ctc-cost-mapping-label {
    color: #747474;
}

.ctc-cost-mapping-body {
    border-top: 1px solid #E8E8E8;
    padding-top: 20px;
    margin-top: 16px;
}

/* Side by side (Annual Year Statement), the two blocks are told apart by the same
   hairline stood on end. Below md they stack, so it is dropped. */
@media (min-width: 768px) {
    .ctc-cost-overhead-col {
        border-left: 1px solid #E8E8E8;
    }
}

.statement-tab-container {
    display: inline-flex;
    background-color: #d2f8f6;
    padding: 5px;
    border: none;
    border-radius: 11px;
}
.statement-tab-btn {
    border: none !important;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 22px !important;
    border-radius: 6px !important;
    box-shadow: none !important;
    position: relative;
    transition: all 0.2s ease;
}
.statement-tab-btn.active {
    background-color: #01524B !important;
    color: #ffffff !important;
}
.statement-tab-btn.inactive {
    background-color: transparent !important;
    color: #01524B !important;
}
.statement-tab-btn.inactive:hover {
    background-color: #bbf7f5 !important;
    color: #01524B !important;
}
.statement-tab-btn:focus,
.statement-tab-btn.focus {
    box-shadow: none !important;
    outline: none !important;
}

.custom-btn-teal {
    background-color: #01524B !important;
    border-color: #01524B !important;
    color: #ffffff !important;
    border-radius: 6px !important;
    font-weight: 600;
    font-size: 14px;
    padding: 8px 16px;
    transition: all 0.2s ease;
}
.custom-btn-teal:hover {
    background-color: #013e36 !important;
    border-color: #013e36 !important;
    color: #ffffff !important;
}

.employee-count-pill {
    background-color: #d2f8f6;
    color: #01524B;
    padding: 8px 16px;
    border-radius: 50px;
    font-weight: 700;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* ---------------------------------------------------------------------------
   Filters & Settings panel — values from the "Annual CTC Reports" frame: a flat
   #F5F5F5 / 16px panel rather than a bordered card, with the fields sitting on
   white inside a teal hairline.
   --------------------------------------------------------------------------- */
.ctc-filter-panel {
    background: #F5F5F5;
    border-radius: 16px;
    padding: 26px 28px;
}

.ctc-filter-title {
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 20px;
    line-height: 30px;
    letter-spacing: -0.02em;
    color: #262626;
}

.ctc-filter-icon {
    color: #004242;
}

/* One row of fields, 38px apart, with Department taking the slack. */
.ctc-filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 20px 38px;
}

.ctc-filter-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1 1 200px;
    min-width: 180px;
}

.ctc-filter-field-wide {
    flex: 2 1 320px;
}

/* The shared .filter-label / .filter-select in _ctc.scss carry the older CTC
   treatment (42px, #a3cbd6 hairline), so this screen's frame values are applied
   over them here rather than changing every CTC screen at once. */
.ctc-filter-panel .filter-label {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: -0.02em;
    color: #01524B;
    margin-bottom: 0;
}

.ctc-filter-panel ::v-deep .filter-select {
    height: 36px !important;
    padding: 0 16px !important;
    background-color: #FFFFFF !important;
    border: 1px solid #01524B !important;
    border-radius: 8px !important;
    font-family: 'Poppins', sans-serif;
    font-weight: 600 !important;
    font-size: 15px !important;
    line-height: 20px !important;
    letter-spacing: -0.02em;
    color: #282828 !important;
}

/* The end of the window is derived, not chosen — the frame greys it out. */
.ctc-filter-panel ::v-deep .filter-select-locked:disabled {
    background-color: #DEDEDE !important;
    color: #282828 !important;
    opacity: 1;
}

/* The panel's two actions are the system buttons (.custom-wizard-btn and the
   plain b-button), which already match the frame — see the template. */

/* ---------------------------------------------------------------------------
   Summary cards — translucent white on a 16px radius, lifted off the page, with
   the value and its caption grouped under the title and a 47px tinted badge on
   the right.
   --------------------------------------------------------------------------- */
.ctc-summary-card {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    padding: 15px 15px 15px 20px;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(1.5px);
    border-radius: 16px;
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);
}

.ctc-summary-body {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 15px;
    min-width: 0;
}

.ctc-summary-title {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 13px;
    line-height: 150%;
    color: #747474;
}

.ctc-summary-value {
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 140%;
    color: #000000;
    word-break: break-word;
}

.ctc-summary-sub {
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 13px;
    line-height: 150%;
    color: #8F8F8F;
}

.ctc-summary-badge {
    width: 47px;
    height: 47px;
    flex-shrink: 0;
    border-radius: 11.29px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}

.ctc-summary-taka {
    font-weight: 700;
    font-size: 20px;
    line-height: 1;
}

.ctc-summary-badge-blue {
    background: rgba(148, 235, 255, 0.5);
    color: #007AFF;
}

.ctc-summary-badge-teal {
    background: #D3F5F2;
    color: #01524B;
}

.ctc-summary-badge-indigo {
    background: #E6E8FF;
    color: #686CCC;
}

.ctc-summary-badge-rose {
    background: #FFE6E8;
    color: #A8555A;
}

/* The variance card reuses the table's red/green pair. */
.ctc-summary-value.ctc-variance-due,
.ctc-summary-sub.ctc-variance-due {
    color: #A8555A;
}

.ctc-summary-value.ctc-variance-settled,
.ctc-summary-sub.ctc-variance-settled {
    color: #167D57;
}

/* The shared .section-heading is teal Outfit; this frame sets the breakdown
   heading in the same Poppins as the page title. */
.ctc-breakdown-heading {
    font-family: 'Poppins', sans-serif !important;
    font-weight: 600 !important;
    font-size: 18px !important;
    line-height: 27px;
    letter-spacing: -0.02em;
    color: #000000 !important;
}

/* Pagination is styled centrally in resources/sass/_ctc.scss. */

/* The search box (.search-input-group) is styled centrally in
   resources/sass/_ctc.scss so every CTC list screen shares one definition. */

/* Custom Upload Modal Styles */
::v-deep .custom-upload-modal .modal-content {
    border-radius: 20px !important;
    border: none !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
    padding: 24px !important;
}

.modal-title-custom {
    color: #0c1a30;
    font-weight: 700 !important;
    font-size: 1.35rem;
}

/* Cross matches the "Create New CTC Item" modal (::v-deep .modal-header .close
   in Configuration.vue): the native × glyph at 1.15rem/#333, half-opacity until
   hover, instead of a b-icon. The offsets are negative because this modal's
   content carries the 24px padding the bootstrap header would otherwise own, so
   they pull the glyph back to the same 1rem/0.75rem inset from the dialog edge. */
.ctc-modal-close {
    position: absolute;
    top: -3px;
    right: 0px;
    margin: 0;
    padding: 0;
    background: transparent;
    border: none;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1;
    color: #333;
    text-shadow: 0 1px 0 #fff;
    opacity: 0.5;
    outline: none;
    cursor: pointer;
    transition: opacity 0.2s;
}
.ctc-modal-close:hover {
    opacity: 0.8;
}

/* "Employee CTC Details" modal title — centred, 18px/22px, per the Figma frame
   (Frame 427319456) */
.ctc-details-modal-title {
    display: block;
    text-align: center;
    color: #212529;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 22px;
    letter-spacing: -0.02em;
}

/* Custom File Input Styles */
.custom-file-input-field ::v-deep .custom-file-label {
    height: 43px;
    line-height: 30px;
    border: 1.5px solid #a3cbd6 !important;
    border-radius: 10px !important;
    color: #727272 !important;
    font-size: 0.95rem;
    padding: 6px 16px;
    background-color: #ffffff !important;
    box-shadow: none !important;
}

.custom-file-input-field ::v-deep .custom-file-label::after {
    height: 41px;
    line-height: 28px;
    background-color: #01524B !important;
    color: #ffffff !important;
    content: "Browse" !important;
    border-left: 1.5px solid #a3cbd6 !important;
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px !important;
    padding: 6px 24px;
    font-weight: 600;
}

/* The upload modal's footer buttons now reuse the Employee Management pair
   (.custom-wizard-btn / plain .btn-secondary), which are defined globally in
   resources/sass/atlhr.scss — no local overrides, so the two screens cannot
   drift apart. */

/* ---------------------------------------------------------------------------
   <common-list> presentation for the mode 1 breakdown. DemoList.vue is shared
   and must not be edited, so its markup is reached from the outside.

   The table itself — header bar, sort arrows, body rows, zebra striping and the
   suppressed column picker / filter trigger — is styled in
   resources/sass/_ctc.scss under `.ctc-annual-list-wrap`, shared with the
   Employees and Audit Logs lists so the three render one identical table. Only
   this screen's column alignment lives here.
   --------------------------------------------------------------------------- */

/* The three default columns come first, then the five appended ones: joining
   date reads centred and the four money columns right-aligned, as before.
   DemoList gives every appended cell Bootstrap's .text-left, which is !important,
   so these have to be too. */
.ctc-annual-list-wrap ::v-deep .employee-list-table tbody td:nth-child(4) {
    text-align: center !important;
}
.ctc-annual-list-wrap ::v-deep .employee-list-table tbody td:nth-child(n+5):nth-child(-n+8) {
    text-align: right !important;
    white-space: nowrap;
}

/* Row menu icons. DemoList chooses a glyph from the action's label and falls back
   to a gear for any label it doesn't know, which is what both of these get, so
   the gear is hidden here and the intended icon drawn as a Font Awesome glyph in
   its place: an eye for Details and a spreadsheet for Export. The two buttons are
   addressed by position because the label never reaches the markup as anything a
   selector can match — the order is the actions array in annualListRows(), and
   the menu holds nothing else. Sizing repeats `.action-dropdown-menu .btn svg`
   from resources/sass/custom.scss so the glyph lands where the SVG sat. */
.ctc-annual-list-wrap ::v-deep .action-dropdown-menu .btn-group > div:nth-child(-n+2) > .btn > svg {
    display: none !important;
}
.ctc-annual-list-wrap ::v-deep .action-dropdown-menu .btn-group > div:nth-child(-n+2) > .btn::before {
    font-family: 'Font Awesome 5 Free';
    /* 400 is Font Awesome's regular face — the outline drawing of the glyph.
       900 would pull the same codepoint from the solid face and fill it in. */
    font-weight: 400;
    font-size: 14px;
    display: inline-block;
    vertical-align: middle;
    margin-top: -2px;
    margin-right: 5px;
}
/* fa-eye */
.ctc-annual-list-wrap ::v-deep .action-dropdown-menu .btn-group > div:nth-child(1) > .btn::before {
    content: '\f06e';
}
/* fa-file-excel */
.ctc-annual-list-wrap ::v-deep .action-dropdown-menu .btn-group > div:nth-child(2) > .btn::before {
    content: '\f1c3';
}

/* The two options in the menu, label and glyph together — the ::before icons
   above inherit this. The menu is a sibling of .action-dropdown-toggle, not a
   child of it, so it has to be reached through .action-dropdown-menu; and
   custom.scss sets `color: #000 !important` on the same buttons, so matching it
   with !important is what makes this land. */
.ctc-annual-list-wrap ::v-deep .action-dropdown-menu .btn {
    color: #4E3E3F !important;
}

</style>

<!--
  Unscoped on purpose: BootstrapVue teleports the modal to document.body, so the
  scope attribute never reaches .modal-header/.modal-body and ::v-deep can't
  match them. Every rule below is namespaced by the modal-class instead.
-->
<style>
/* Employee CTC Details modal — 1464px frame with 50px padding all round and a
   50px gap between the title and the content, per the Figma frame. */
.ctc-details-modal .modal-dialog {
    max-width: 1464px;
    width: calc(100vw - 40px);
}

.ctc-details-modal .modal-content {
    border-radius: 4px !important;
    border: none !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
}

.ctc-details-modal .modal-header {
    border-bottom: none !important;
    padding: 50px 50px 0 50px !important;
    /* The title is centred across the frame; the close button is pinned to the
       top-right corner rather than sharing the flex row. */
    justify-content: center !important;
    position: relative;
}

.ctc-details-modal .modal-title {
    text-align: center !important;
    width: 100% !important;
    margin: 0 !important;
}

/* Pinned flush into the modal's top-right corner, the way every other dialog
   in the system places its close cross, instead of sitting beside the title. */
.ctc-details-modal .modal-header .close {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem !important;
    line-height: 1 !important;
    color: #333 !important;
    opacity: 0.5;
    outline: none !important;
    padding: 0 !important;
    margin: 0 !important;
}

.ctc-details-modal .modal-header .close:hover {
    opacity: 0.8;
}

.ctc-details-modal .modal-body {
    padding: 50px !important;
}

/* Variance cells inside <common-list>: red while an amount is still owed, green
   once it is settled, per the "Annual CTC Reports" frame. The classes are put on
   the cells by paintVarianceColumn(), and these rules sit in the unscoped block
   because the markup belongs to the child component. */
.ctc-annual-list-wrap td.ctc-variance-due,
.ctc-annual-list-wrap td.ctc-variance-due span {
    color: #A8555A !important;
}

.ctc-annual-list-wrap td.ctc-variance-settled,
.ctc-annual-list-wrap td.ctc-variance-settled span {
    color: #167D57 !important;
}

.ctc-annual-list-wrap td.ctc-variance-due span,
.ctc-annual-list-wrap td.ctc-variance-settled span {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 13px;
    line-height: 140%;
    white-space: nowrap;
}

</style>
